import type { ExecutionEnvironmentPlatformOs } from "@afar-research/contracts";

/**
 * Where the thread's Environment runs, as far as picking files cares. Only on
 * this machine does a path from the client's own disk name the same file
 * there, and only on this Mac can the host open its native panel for us.
 */
export type ComposerEnvironmentMachine = "this-mac" | "this-machine" | "elsewhere";

export function composerEnvironmentMachine(input: {
  readonly sharesClientMachine: boolean;
  readonly hostOs: ExecutionEnvironmentPlatformOs | null;
}): ComposerEnvironmentMachine {
  if (!input.sharesClientMachine) return "elsewhere";
  return input.hostOs === "darwin" ? "this-mac" : "this-machine";
}

export type ComposerFilesAndFoldersOpenStrategy =
  | { readonly kind: "desktop-mixed" }
  | { readonly kind: "host-native-mixed" }
  | { readonly kind: "browser-file-input" };

/**
 * The path pickers name files on the client's own disk, so they are only used
 * when the client shares the Environment's machine:
 *
 * - Desktop: async Electron dialog (files+folders, non-blocking).
 * - Host Web on a Mac host: async host NSOpenPanel (files+folders, non-blocking).
 *
 * Everywhere else, including every remote or cloud Environment, the browser
 * file input picks files whose bytes are uploaded to the Environment.
 */
export function resolveComposerFilesAndFoldersOpenStrategy(input: {
  readonly hasDesktopPickAttachments: boolean;
  readonly environmentMachine: ComposerEnvironmentMachine;
}): ComposerFilesAndFoldersOpenStrategy {
  if (input.environmentMachine === "elsewhere") return { kind: "browser-file-input" };
  if (input.hasDesktopPickAttachments) return { kind: "desktop-mixed" };
  if (input.environmentMachine === "this-mac") return { kind: "host-native-mixed" };
  return { kind: "browser-file-input" };
}
